import {
  extractHeaderBenchmarkFields,
  TXN_BENCHMARK_COLUMN_KEYS,
  withTxnBenchmarkFields,
} from "../reports/transactionBenchmarkColumns";
import { getDisplayableReturnMetric } from "../returnMetrics";

import {
  TXN_METADATA_COLUMN_KEYS,
  withTxnMetadataFields,
} from "./transactionMetadataColumns";

const TXN_METADATA_COLUMN_KEY_SET = new Set<string>(TXN_METADATA_COLUMN_KEYS);

const HEADER_BENCHMARK_KEY_SET = new Set<string>(TXN_BENCHMARK_COLUMN_KEYS);

/** Columns whose numeric values should be summed in CSV export totals row. */
const SUMMABLE_KEY_ALIASES = new Set([
  "amount",
  "costvalue",
  "investedamount",
  "invested_amount",
  "amount_invested",
  "netpurchase",
  "net_purchase",
  "currentvalue",
  "currentamount",
  "unrealisedpnl",
  "unrealizedpnl",
  "unrealisedgainloss",
  "unrealizedgainloss",
  "realisedpnl",
  "realizedpnl",
  "realisedgainloss",
  "realizedgainloss",
  "gainloss",
  "marketvalue",
  "absolutereturn",
  "absolutepl",
]);

const NON_SUMMABLE_KEY_FRAGMENTS = [
  "percentage",
  "perc",
  "%",
  "nav",
  "units",
  "unit",
  "folio",
  "cagr",
  "xirr",
  "isin",
  "symbol",
  "scheme",
  "member",
  "date",
  "type",
  "category",
  "broker",
  "days",
  "roi",
  "tenure",
  "combined",
  "benchmark",
  "nifty",
  "bsesensex",
  "marketcap",
  "price",
];

export function parseReportCsvNumeric(value: unknown): number | null {
  if (value === null || value === undefined) return null;
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : null;
  }

  const str = String(value).trim();
  if (!str || str === "-" || str === "—") return null;

  const primary = str.split("/")[0].trim();
  const isParenNegative =
    primary.startsWith("(") && primary.endsWith(")") && primary.length > 2;
  const withoutParens = isParenNegative ? primary.slice(1, -1).trim() : primary;

  const match = withoutParens.match(/-?(?:\d{1,3}(?:,\d{3})*|\d+)(?:\.\d+)?/);
  if (!match) return null;

  const num = Number(match[0].replace(/,/g, ""));
  if (!Number.isFinite(num)) return null;
  return isParenNegative ? -num : num;
}

export function isSummableReportColumn(col: {
  key: string;
  value?: string;
}): boolean {
  const key = col.key.trim();
  const keyLower = key.toLowerCase();
  const label = String(col.value ?? "").toLowerCase();

  if (TXN_METADATA_COLUMN_KEY_SET.has(key)) {
    return false;
  }

  if (NON_SUMMABLE_KEY_FRAGMENTS.some((frag) => keyLower.includes(frag))) {
    return false;
  }
  if (NON_SUMMABLE_KEY_FRAGMENTS.some((frag) => label.includes(frag))) {
    return false;
  }

  // Any column keyed as "amount" (e.g. PMS / bond txn amount fields).
  if (keyLower === "amount") {
    return true;
  }

  if (/amount/i.test(label)) {
    return true;
  }

  // Prefer explicit known financial amount keys.
  if (SUMMABLE_KEY_ALIASES.has(keyLower)) return true;

  // Otherwise, allow summing for any column that isn't explicitly excluded.
  // The totals builder will still skip columns that have no numeric values.
  return true;
}

const firstParsedAmount = (...sources: unknown[]): number | null => {
  for (const raw of sources) {
    const parsed = parseReportCsvNumeric(raw);
    if (parsed !== null) return parsed;
  }
  return null;
};

const TOTAL_HOLDING_CAGR_KEYS = [
  "cagr",
  "cagrMF",
  "cagrMf",
  "cagrMFAumCapital",
  "cagrAif",
  "cagrBond",
  "cagrStock",
  "cagrSif",
  "cagrPms",
  "cagrPrivateStock",
  "cagrIntlEquity",
  "cagrReit",
  "cagrInsurance",
  "cagrOther",
  "cagrFD",
] as const;

const parseTotalHoldingCagr = (
  info: Record<string, unknown>,
): number | null => {
  for (const key of TOTAL_HOLDING_CAGR_KEYS) {
    const displayable = getDisplayableReturnMetric(
      info[key] as string | number | null | undefined,
    );
    if (displayable !== null) return displayable;
  }
  return null;
};

/** Use API field as-is — never derive P&L from current/invested or interest rate. */
const pickBackendField = (...sources: unknown[]): unknown => {
  for (const raw of sources) {
    if (raw === null || raw === undefined) continue;
    if (typeof raw === "string" && raw.trim() === "") continue;
    if (raw === "-") continue;
    return raw;
  }
  return undefined;
};

/** Align holdings API row fields for report tables and row-sum fallbacks. */
export function normalizeHoldingReportRow(
  row: Record<string, any>,
): Record<string, any> {
  const meta = row.metadata ?? row.meta ?? {};
  const invested =
    row.investedAmount ??
    row.costValue ??
    row.amount ??
    row.purchaseValue ??
    row.netPurchase ??
    row.principal ??
    row.invested_amount ??
    meta.investedAmount ??
    meta.costValue;
  const current =
    row.currentAmount ??
    row.currentValue ??
    row.marketValue ??
    row.maturityAmount ??
    row.returnAmount ??
    row.maturity_amount ??
    meta.currentAmount ??
    meta.currentValue;
  const gainLoss = pickBackendField(
    row.gainLoss,
    meta.gainLoss,
    row.unrealisedGainLoss,
    row.unrealizedGainLoss,
    row.unrealisedPNL,
    row.unrealisedPnL,
    row.unrealizedPNL,
    row.unrealizedPnl,
  );

  const gainLossPercentage = pickBackendField(
    row.gainLossPercentage,
    row.gainLossPerc,
    meta.gainLossPercentage,
    row.unrealisedGainLossPercentage,
    row.unrealizedGainLossPercentage,
    row.unrealisedPNLPercentage,
    row.unrealisedPnLPercentage,
    row.unrealizedPNLPercentage,
  );

  return withTxnMetadataFields(
    withTxnBenchmarkFields({
      ...row,
      isin: row.isin ?? row.ISIN ?? meta.isin ?? meta.ISIN ?? row.isin,
      investedAmount: invested ?? row.investedAmount,
      costValue: row.costValue ?? invested,
      amount: row.amount ?? invested,
      currentAmount: current ?? row.currentAmount,
      currentValue: row.currentValue ?? current,
      marketValue: row.marketValue ?? current,
      adjustedCostPrice:
        row.adjustedCostPrice ?? meta.adjustedCostPrice ?? row.adjusted_cost_price,
      gainLoss: gainLoss ?? row.gainLoss,
      unrealisedPNL:
        row.unrealisedPNL ??
        row.unrealisedPnL ??
        row.unrealizedPNL ??
        gainLoss ??
        row.unrealisedPNL,
      gainLossPercentage: gainLossPercentage ?? row.gainLossPercentage,
      gainLossPerc: row.gainLossPerc ?? gainLossPercentage ?? row.gainLossPerc,
    }),
  );
}

/** Portfolio / benchmark CAGR from holdings API (`totalHoldingInfo`). */
const parseTotalHoldingBenchmarkCagr = (
  info: Record<string, unknown>,
  flatKeys: string[],
  nestedKeys: string[],
): number | null => {
  for (const key of flatKeys) {
    const displayable = getDisplayableReturnMetric(
      info[key] as string | number | null | undefined,
    );
    if (displayable !== null) return displayable;
  }
  for (const nestedKey of nestedKeys) {
    const block = info[nestedKey] as Record<string, unknown> | undefined;
    if (!block || typeof block !== "object") continue;
    const displayable = getDisplayableReturnMetric(
      block.cagr as string | number | null | undefined,
    );
    if (displayable !== null) return displayable;
  }
  return null;
};

/** Portfolio totals from holdings API (`totalHoldingInfo`). */
export function parseReportTotalHoldingSummary(
  info: Record<string, unknown> | null | undefined,
): {
  totalInvested: number | null;
  currentValue: number | null;
  totalPnl: number | null;
  overallReturnPct: number | null;
  cagr: number | null;
  niftyCagr: number | null;
  sensexCagr: number | null;
  nifty50GainLoss: number | null;
  nifty50GainLossPercentage: number | null;
  bseSensexGainLoss: number | null;
  bseSensexGainLossPercentage: number | null;
} | null {
  if (!info || typeof info !== "object") return null;

  const headerBenchmarks = extractHeaderBenchmarkFields(info as Record<string, any>);

  const totalInvested = firstParsedAmount(
    info.totalInvested,
    info.totalValueInvested,
    info.totalValueInvestedMF,
    info.totalValueInvestedMFAumCapital,
    info.totalValueInvestedOther,
    info.totalValueInvestedInsurance,
    info.totalValueAtCost,
    info.investedAmount,
    info.totalInvestedAmount,
  );
  const currentValue = firstParsedAmount(
    info.currentValue,
    info.currentValueMF,
    info.currentValueMFAumCapital,
    info.currentAmount,
    info.totalCurrentValue,
    info.totalFinalValue,
    info.totalFinalValueOther,
    info.totalFinalValueInsurance,
    info.maturityAmount,
    info.marketValue,
  );
  const totalPnl = firstParsedAmount(
    info.gainLoss,
    info.unrealisedGainLoss,
    info.unrealisedGainLossMF,
    info.unrealisedGainLossMFAumCapital,
    info.unrealisedGainLossOther,
    info.unrealisedGainLossInsurance,
    info.unrealizedGainLoss,
    info.unrealisedPNL,
    info.unrealisedPnL,
    info.unrealizedPNL,
    info.unrealizedPnl,
    info.absoluteReturn,
    info.absolutePL,
    info.totalGainLoss,
  );
  const overallReturnPct = firstParsedAmount(
    info.gainLossPercentage,
    info.gainLossPerc,
    info.roi,
    info.roiMF,
    info.roiMFAumCapital,
    info.roiOther,
    info.roiInsurance,
    info.unrealisedGainLossPercentage,
    info.unrealizedGainLossPercentage,
    info.unrealisedPNLPercentage,
    info.unrealisedPnLPercentage,
    info.unrealizedPNLPercentage,
    info.absoluteReturnPerc,
  );
  const cagr = parseTotalHoldingCagr(info);
  const niftyCagr = parseTotalHoldingBenchmarkCagr(
    info,
    ["niftyCagr", "nifty50Cagr", "nifty50CAGR"],
    ["nifty50", "Nifty50"],
  );
  const sensexCagr = parseTotalHoldingBenchmarkCagr(
    info,
    ["sensexCagr", "bseSensexCagr", "sensexCAGR"],
    ["bseSensex", "BSESensex"],
  );

  if (
    totalInvested === null &&
    currentValue === null &&
    totalPnl === null &&
    cagr === null &&
    niftyCagr === null &&
    sensexCagr === null &&
    headerBenchmarks.nifty50GainLossPercentage === null &&
    headerBenchmarks.bseSensexGainLossPercentage === null
  ) {
    return null;
  }

  return {
    totalInvested,
    currentValue,
    totalPnl,
    overallReturnPct,
    cagr,
    niftyCagr,
    sensexCagr,
    nifty50GainLoss: headerBenchmarks.nifty50GainLoss,
    nifty50GainLossPercentage: headerBenchmarks.nifty50GainLossPercentage,
    bseSensexGainLoss: headerBenchmarks.bseSensexGainLoss,
    bseSensexGainLossPercentage: headerBenchmarks.bseSensexGainLossPercentage,
  };
}

export function formatReportCsvTotal(value: number): string {
  return value.toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

/** ASCII currency marker for Excel CSV (₹ often becomes mojibake). */
export const REPORT_CSV_CURRENCY_PREFIX = "Rs ";

/**
 * Format a numeric amount for report CSV: "Rs 1,234.56" (ASCII-safe for Excel).
 * Negatives: "Rs -1,234.56"
 */
export function formatReportCsvInr(value: unknown): string {
  const parsed = parseReportCsvNumeric(value);
  if (parsed === null) return "-";
  const abs = Math.abs(parsed).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return parsed < 0
    ? `${REPORT_CSV_CURRENCY_PREFIX}-${abs}`
    : `${REPORT_CSV_CURRENCY_PREFIX}${abs}`;
}

/**
 * Excel often misreads UTF-8 ₹ in CSV as mojibake — use ASCII "Rs " in exports only.
 * Also strips common Windows-1252 mojibake for ₹ (â‚¹).
 */
export function sanitizeReportCsvCurrency(text: string): string {
  return String(text ?? "")
    .replace(/\u20B9\s*/g, REPORT_CSV_CURRENCY_PREFIX) // ₹ → "Rs "
    .replace(/â‚¹\s*/g, REPORT_CSV_CURRENCY_PREFIX)
    .replace(/Rs(?=-?\d)/g, REPORT_CSV_CURRENCY_PREFIX) // "Rs123" / "Rs-123" → "Rs …"
    .replace(/Rs\s+/g, REPORT_CSV_CURRENCY_PREFIX);
}

/** Prefixed UTF-8 BOM so Excel on Windows detects encoding correctly. */
export function withExcelCsvBom(csvContent: string): string {
  return `\uFEFF${sanitizeReportCsvCurrency(csvContent)}`;
}

const CURRENT_VALUE_TOTAL_KEYS = new Set([
  "currentamount",
  "currentvalue",
  "marketvalue",
]);

export function buildReportCsvTotalsRow(
  columns: { key: string; value?: string }[],
  rows: Record<string, unknown>[],
  headerInfo?: Record<string, unknown> | null,
): string | null {
  if (!columns.length || !rows.length) return null;

  const headerBenchmarks = headerInfo
    ? extractHeaderBenchmarkFields(headerInfo as Record<string, any>)
    : null;

  const cells = columns.map((col, index) => {
    if (index === 0) return '"Total"';

    if (HEADER_BENCHMARK_KEY_SET.has(col.key)) {
      if (!headerBenchmarks) return '"-"';
      const val = headerBenchmarks[col.key as keyof typeof headerBenchmarks];
      if (val === null) return '"-"';
      return `"${formatReportCsvTotal(val)}"`;
    }

    if (!isSummableReportColumn(col)) return '""';

    let sum = 0;
    let sawAny = false;
    const keyLower = col.key.trim().toLowerCase();
    // Holding-level current stamped on every PMS txn — sum once per holding.
    const dedupeByHolding =
      CURRENT_VALUE_TOTAL_KEYS.has(keyLower) &&
      rows.some((row) => row?.holdingId != null && row?.holdingId !== "");

    if (dedupeByHolding) {
      const seenHoldings = new Set<string>();
      for (const row of rows) {
        const hid = row?.holdingId;
        if (hid == null || hid === "") continue;
        const key = String(hid);
        if (seenHoldings.has(key)) continue;
        seenHoldings.add(key);
        const parsed = parseReportCsvNumeric(row?.[col.key]);
        if (parsed === null) continue;
        sum += parsed;
        sawAny = true;
      }
    } else {
      for (const row of rows) {
        const parsed = parseReportCsvNumeric(row?.[col.key]);
        if (parsed === null) continue;
        sum += parsed;
        sawAny = true;
      }
    }

    if (!sawAny) return '""';
    // Prefer "Rs …" for money totals so Excel matches data rows.
    const looksLikeMoney =
      CURRENT_VALUE_TOTAL_KEYS.has(keyLower) ||
      keyLower.includes("amount") ||
      keyLower.includes("value") ||
      keyLower.includes("pnl") ||
      keyLower.includes("gain") ||
      keyLower.includes("cost") ||
      keyLower.includes("price") ||
      keyLower === "stt" ||
      keyLower === "brokerage" ||
      keyLower === "stampduty" ||
      keyLower === "otherexpenses" ||
      keyLower === "dividend" ||
      keyLower === "interest" ||
      keyLower === "capitalrepayment" ||
      keyLower === "othercomponents";
    const totalText = looksLikeMoney
      ? formatReportCsvInr(sum)
      : formatReportCsvTotal(sum);
    return `"${totalText.replace(/"/g, '""')}"`;
  });

  // If we didn't actually sum any numeric column, don't add a totals row.
  const hasAnyTotals = cells.some(
    (cell, idx) => idx !== 0 && cell !== '""' && cell !== '"-"'
  );
  return hasAnyTotals ? cells.join(",") : null;
}
